import { type MouseEvent, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { assert } from '@chainflip/utils/assertion';
import { type BaseChainflipAsset, type ChainflipAsset } from '@chainflip/utils/chainflip';
import { isNotNullish, isNullish } from '@chainflip/utils/guard';
import { useMutation } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { DashboardModal } from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { TabSelect } from '@/shared/components/TabSelect';
import TokenInputField from '@/shared/components/TokenInputField';
import { useActiveStrategies } from '@/shared/hooks/useActiveStrategies';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import { useStrategyLimits } from '@/shared/hooks/useStrategyLimits';
import { useStrategyTemplates } from '@/shared/hooks/useStrategyTemplates';
import useTracking from '@/shared/hooks/useTracking';
import CloseIcon from '@/shared/icons/flip-ui-kit/large/Close';
import WindowIcon from '@/shared/icons/flip-ui-kit/large/Window';
import WarningTriangleIcon from '@/shared/icons/flip-ui-kit/small/WarningTriangle';
import { chainflipAssetMap } from '@/shared/utils/env';
import {
  getStrategyTickKey,
  getStrategyTemplateKey,
  getStrategyTemplateUrl,
  customStrategyPriceToTick,
  type AllowedStrategyType,
  assetSupportsStrategyType,
  validateStrategySpread,
  formatTick,
  bpsToTick,
  maxWholeUnits,
  TOOLTIP_COPY,
} from '@/shared/utils/strategies';
import TokenAmount from '@/shared/utils/TokenAmount';
import { NewStrategyOverview } from './NewStrategyOverview';
import SelectTokenModal from './SelectTokenModal';
import SpreadInputs from './SpreadInputs';
import { StrategyOverview } from './StrategyOverview';
import { StrategySuccessModal } from './StrategySuccessModal';
import { useExtrinsicSDK, useStateChainAccount } from '../../hooks/useStateChainAccount';
import { LiquidityProvisionEvents, type LiquidityProvisionTrackEvents } from '../../types/track';
import { LpFundAccountCallout } from '../LpFundAccountButton';

const STRATEGY_TYPE_TABS = [
  { label: 'Oracle-based', value: 'ORACLE_TRACKING' },
  { label: 'Hard peg', value: 'INVENTORY_BASED' },
] as const;

const STRATEGY_DESCRIPTION = {
  INVENTORY_BASED:
    'Your orders are placed at the exact price points you set — buy orders below peg, sell orders above. Ranges stay fixed until you update them. No lockups; withdraw anytime.',
  ORACLE_TRACKING:
    'Your liquidity range automatically follows the oracle price as it moves — so your orders stay active without manual adjustment. No lockups; withdraw anytime.',
} as const;

type Props = {
  open: boolean;
  onClose: () => void;
};

export type StrategyInitialInfo = {
  initialStrategyType: AllowedStrategyType;
  initialBaseAsset: BaseChainflipAsset;
  initialMinBuyTick: number;
  initialMaxBuyTick: number;
  initialMinSellTick: number;
  initialMaxSellTick: number;
};

const DEFAULT_INITIAL_INFO = {
  initialStrategyType: 'ORACLE_TRACKING',
  initialBaseAsset: undefined,
  initialMinBuyTick: -1,
  initialMaxBuyTick: 0,
  initialMinSellTick: 0,
  initialMaxSellTick: 1,
} as const;

const readInitialValues = (initialData: StrategyInitialInfo | Record<string, undefined>) => ({
  initialStrategyType: initialData.initialStrategyType ?? DEFAULT_INITIAL_INFO.initialStrategyType,
  initialBaseAsset: initialData.initialBaseAsset ?? DEFAULT_INITIAL_INFO.initialBaseAsset,
  initialMinBuyTick: initialData.initialMinBuyTick ?? DEFAULT_INITIAL_INFO.initialMinBuyTick,
  initialMaxBuyTick: initialData.initialMaxBuyTick ?? DEFAULT_INITIAL_INFO.initialMaxBuyTick,
  initialMinSellTick: initialData.initialMinSellTick ?? DEFAULT_INITIAL_INFO.initialMinSellTick,
  initialMaxSellTick: initialData.initialMaxSellTick ?? DEFAULT_INITIAL_INFO.initialMaxSellTick,
});

export default function CreateStrategyModal({
  open,
  onClose,
  ...strategyInfo
}: Props | (Props & StrategyInitialInfo)) {
  const track = useTracking<LiquidityProvisionTrackEvents>();
  const { sendExtrinsic: sendDeployStrategyExtrinsic, toast: deployStrategyToast } =
    useExtrinsicSDK('deployStrategy');
  const { sendExtrinsic: sendAddFundsExtrinsic, toast: addFundsToast } =
    useExtrinsicSDK('addFundsToStrategy');

  const { freeBalances } = useStateChainAccount();
  const { selectedAccount } = usePolkadot(true);

  const quoteAsset = 'Usdc' as ChainflipAsset;
  const [displayTokenModal, setDisplayTokenModal] = useState<boolean>(false);

  const {
    initialStrategyType,
    initialBaseAsset,
    initialMinBuyTick,
    initialMaxBuyTick,
    initialMinSellTick,
    initialMaxSellTick,
  } = readInitialValues(strategyInfo);

  const getDefaults = (type: AllowedStrategyType) => ({
    minBuy: formatTick(initialMinBuyTick, type, initialBaseAsset),
    maxBuy: formatTick(initialMaxBuyTick, type, initialBaseAsset),
    minSell: formatTick(initialMinSellTick, type, initialBaseAsset),
    maxSell: formatTick(initialMaxSellTick, type, initialBaseAsset),
  });

  const [selectedStrategyType, setSelectedStrategyType] = useState(initialStrategyType);
  const [baseAsset, setBaseAsset] = useState(initialBaseAsset);
  const [baseAssetAmount, setBaseAssetAmount] = useState<TokenAmount>();
  const [quoteAssetAmount, setQuoteAssetAmount] = useState<TokenAmount>();

  const initialDefaults = getDefaults(selectedStrategyType);
  const [minBuy, setMinBuy] = useState(initialDefaults.minBuy);
  const [maxBuy, setMaxBuy] = useState(initialDefaults.maxBuy);
  const [minSell, setMinSell] = useState(initialDefaults.minSell);
  const [maxSell, setMaxSell] = useState(initialDefaults.maxSell);

  const [baseInputError, setBaseInputError] = useState<string>();
  const [quoteInputError, setQuoteInputError] = useState<string>();

  const validation = useMemo(
    () => validateStrategySpread(selectedStrategyType, { minBuy, maxBuy, minSell, maxSell }),
    [minBuy, maxBuy, minSell, maxSell, selectedStrategyType],
  );

  const resetData = () => {
    const resetDefaults = getDefaults(initialStrategyType);
    setDisplayTokenModal(false);
    setSelectedStrategyType(initialStrategyType);
    setBaseAsset(initialBaseAsset);
    setMinBuy(resetDefaults.minBuy);
    setMaxBuy(resetDefaults.maxBuy);
    setMinSell(resetDefaults.minSell);
    setMaxSell(resetDefaults.maxSell);
    setBaseAssetAmount(undefined);
    setQuoteAssetAmount(undefined);
    setBaseInputError(undefined);
    setQuoteInputError(undefined);
  };

  useEffect(() => {
    setBaseInputError(undefined);
    setQuoteInputError(undefined);
  }, [quoteAssetAmount, baseAssetAmount]);

  const { data: limitsData } = useStrategyLimits();
  const baseMinimumDeposit =
    (baseAsset && limitsData?.[baseAsset]?.minimumDeploymentAmount) ?? TokenAmount.ZERO;
  const quoteMinimumDeposit = limitsData?.[quoteAsset]?.minimumDeploymentAmount ?? TokenAmount.ZERO;
  const minimumDeposit = maxWholeUnits(baseMinimumDeposit, quoteMinimumDeposit); // not sure if this is the protocol behavior
  const baseMinimumAddedFunds =
    (baseAsset && limitsData?.[baseAsset]?.minimumAddedFundsAmount) ?? TokenAmount.ZERO;
  const quoteMinimumAddedFunds =
    limitsData?.[quoteAsset]?.minimumAddedFundsAmount ?? TokenAmount.ZERO;
  const minimumAddedFunds = maxWholeUnits(baseMinimumAddedFunds, quoteMinimumAddedFunds); // not sure if this is the protocol behavior

  const tickConverter = (price: string, asset: BaseChainflipAsset) =>
    selectedStrategyType === 'INVENTORY_BASED'
      ? customStrategyPriceToTick(price, asset)
      : bpsToTick(price);
  const templateKey =
    baseAsset &&
    isNotNullish(minBuy) &&
    isNotNullish(maxBuy) &&
    isNotNullish(minSell) &&
    isNotNullish(maxSell)
      ? getStrategyTemplateKey({
          baseAsset,
          type: selectedStrategyType,
          key: getStrategyTickKey({
            minBuyTick: tickConverter(minBuy, baseAsset),
            maxBuyTick: tickConverter(maxBuy, baseAsset),
            minSellTick: tickConverter(minSell, baseAsset),
            maxSellTick: tickConverter(maxSell, baseAsset),
          }),
        })
      : undefined;
  const { data: activeStrategies } = useActiveStrategies();
  const templatesData = useStrategyTemplates(activeStrategies, selectedAccount);
  const matchingTemplate = templatesData?.all.find((t) => t.templateKey === templateKey);
  const [matchingTemplateBefore, setMatchingTemplateBefore] = useState(matchingTemplate);

  const existingInstance = matchingTemplate?.ownInstance;
  const [existingInstanceBefore, setExistingInstanceBefore] = useState(existingInstance);

  const router = useRouter();

  const {
    mutate,
    isPending,
    isSuccess,
    data: mutationData,
    reset: resetMutation,
  } = useMutation({
    mutationFn: async (_: MouseEvent<HTMLButtonElement>) => {
      assert(minimumDeposit, 'minimum deposit is not set');
      assert(minimumAddedFunds, 'minimum added funds is not set');

      if (!baseAsset) {
        setBaseInputError('No asset selected');
        return Promise.reject();
      }

      if (validation.message) return Promise.reject();

      const totalAmount = BigNumber.sum(
        baseAssetAmount?.toBigNumber() ?? 0,
        quoteAssetAmount?.toBigNumber() ?? 0,
      );

      if (!existingInstance && minimumDeposit.toBigNumber().gt(totalAmount)) {
        setBaseInputError(`Total asset amount must be greater than ${minimumDeposit.toFixed()}`);
        setQuoteInputError(`Total asset amount must be greater than ${minimumDeposit.toFixed()}`);
        return Promise.reject();
      }

      if (existingInstance && minimumAddedFunds.toBigNumber().gt(totalAmount)) {
        setBaseInputError(`Total asset amount must be greater than ${minimumAddedFunds.toFixed()}`);
        setQuoteInputError(
          `Total asset amount must be greater than ${minimumAddedFunds.toFixed()}`,
        );
        return Promise.reject();
      }

      assert(minBuy, 'minBuy is not set');
      assert(maxBuy, 'maxBuy is not set');
      assert(minSell, 'minSell is not set');
      assert(maxSell, 'maxSell is not set');

      setMatchingTemplateBefore(matchingTemplate);
      setExistingInstanceBefore(existingInstance);

      if (existingInstance) {
        await sendAddFundsExtrinsic(
          existingInstance.strategyAccountSs58Id,
          [
            [baseAsset, baseAssetAmount?.toString() ?? '0'],
            [quoteAsset, quoteAssetAmount?.toString() ?? '0'],
          ].filter(([, amount]) => amount !== '0') as [ChainflipAsset, string][],
        );
        return { strategyData: { strategyId: existingInstance.strategyAccountSs58Id } };
      }

      const props = {
        path: window.location.pathname,
        scAccount: selectedAccount.idSs58,
        key: templateKey ?? '',
        amount: baseAssetAmount?.toString() ?? '',
        asset: baseAsset,
        estApy: matchingTemplate?.reportingData?.apyPercent?.toString() ?? '0',
      };

      if (matchingTemplate) {
        // when copied
        track(LiquidityProvisionEvents.CtaCopyAssStrategy, {
          props: {
            ...props,
            position: 'ass_copy_modal',
          },
        });
      } else {
        track(LiquidityProvisionEvents.CtaCreateAssStrategy, {
          props: {
            ...props,
            position: 'ass_create_modal',
          },
        });
      }
      return sendDeployStrategyExtrinsic(
        selectedStrategyType === 'INVENTORY_BASED'
          ? {
              type: 'INVENTORY_BASED',
              minBuyTick: customStrategyPriceToTick(minBuy, baseAsset),
              maxBuyTick: customStrategyPriceToTick(maxBuy, baseAsset),
              minSellTick: customStrategyPriceToTick(minSell, baseAsset),
              maxSellTick: customStrategyPriceToTick(maxSell, baseAsset),
              baseAsset,
            }
          : {
              type: 'ORACLE_TRACKING',
              minBuyOffsetTick: bpsToTick(minBuy),
              maxBuyOffsetTick: bpsToTick(maxBuy),
              minSellOffsetTick: bpsToTick(minSell),
              maxSellOffsetTick: bpsToTick(maxSell),
              baseAsset,
              quoteAsset,
            },
        [
          [baseAsset, baseAssetAmount?.toString() ?? '0'],
          [quoteAsset, quoteAssetAmount?.toString() ?? '0'],
        ].filter(([, amount]) => amount !== '0') as [ChainflipAsset, string][],
      );
    },
    onSuccess() {
      if (selectedAccount) {
        track(LiquidityProvisionEvents.CreateAssStrategySuccess, {
          props: {
            path: window.location.pathname,
            position: 'ass_copy_modal|ass_create_modal',
            scAccount: selectedAccount.idSs58,
            key: templateKey ?? '',
            amount: baseAssetAmount?.toString() ?? '',
            asset: baseAsset ?? '',
            estApy: existingInstanceBefore?.reportingData?.apyPercent.toString() ?? '',
          },
        });
      }
    },
    onError(err) {
      if (!(err instanceof Error)) return;
      const toast = existingInstance ? addFundsToast : deployStrategyToast;
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });

  const onTokenSelect = (token: ChainflipToken) => {
    setBaseAsset(token.chainflipId as BaseChainflipAsset);
    setDisplayTokenModal(false);
  };

  if (displayTokenModal)
    return (
      <SelectTokenModal
        open={displayTokenModal}
        onTokenSelect={onTokenSelect}
        onClose={() => setDisplayTokenModal(false)}
        onlyStablecoin
        strategyType={selectedStrategyType}
      />
    );

  if (isSuccess && baseAsset && templateKey) {
    return (
      <StrategySuccessModal
        open={open}
        onClose={onClose}
        afterLeave={() => {
          resetMutation();
          resetData();
        }}
        title={existingInstanceBefore ? `Liquidity Added` : `Strategy Created`}
        baseAsset={baseAsset}
      >
        {existingInstanceBefore ? (
          <StrategyOverview
            strategy={existingInstanceBefore}
            apyPercent={existingInstanceBefore.reportingData?.apyPercent}
            newBaseAssetAmount={
              baseAssetAmount && existingInstanceBefore.baseLiquidity.add(baseAssetAmount)
            }
            newQuoteAssetAmount={
              quoteAssetAmount && existingInstanceBefore.quoteLiquidity.add(quoteAssetAmount)
            }
          />
        ) : (
          <StrategyOverview
            strategy={{
              baseLiquidity: baseAssetAmount ?? TokenAmount.ZERO,
              quoteLiquidity: quoteAssetAmount ?? TokenAmount.ZERO,
              templateKey,
            }}
            apyPercent={matchingTemplateBefore?.reportingData?.apyPercent}
          />
        )}
        <Button
          className="w-full"
          type="primary-standard"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
            router.push(`/strategies/instance/${mutationData.strategyData.strategyId}`);
          }}
        >
          View Strategy Details
        </Button>
        {existingInstanceBefore && (
          <Button
            className="flex w-full items-center gap-x-1"
            type="secondary-standard"
            onClick={() => {
              window.open(
                new URL(
                  getStrategyTemplateUrl(
                    existingInstanceBefore ?? {
                      templateKey,
                    },
                  ),
                  process.env.NEXT_PUBLIC_EXPLORER_URL,
                ),
              );
            }}
          >
            View on Explorer
            <WindowIcon />
          </Button>
        )}
      </StrategySuccessModal>
    );
  }

  return (
    <DashboardModal
      open={open}
      onClose={onClose}
      afterLeave={() => {
        resetData();
        resetMutation();
      }}
    >
      <div className="flex w-[487px] flex-col gap-6 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-6">
        <div className="flex w-full items-start justify-between">
          <div className="flex flex-col font-aeonikMedium">
            <span className="text-24 text-cf-white">Create a strategy</span>
            <div className="text-14 text-cf-light-2">
              The minimum deposit value for any strategy is $
              {minimumDeposit?.toFormatted('numeral')}
            </div>
          </div>
          <button type="button" onClick={onClose} className="outline-cf-gray-5" tabIndex={-1}>
            <CloseIcon />
          </button>
        </div>
        <div className="-mt-2 h-px w-full bg-cf-gray-4" />
        <TabSelect
          pillHeight={26}
          tabs={STRATEGY_TYPE_TABS.map((t) => ({
            ...t,
            disabled: isPending && t.value !== selectedStrategyType,
          }))}
          selectedTab={STRATEGY_TYPE_TABS.find((t) => t.value === selectedStrategyType)!}
          onSelectionChange={(tab) => {
            if (baseAsset && !assetSupportsStrategyType(tab.value, baseAsset)) {
              setBaseAsset(undefined);
            }
            const changeDefaults = getDefaults(tab.value);
            setMinBuy(changeDefaults.minBuy);
            setMaxBuy(changeDefaults.maxBuy);
            setMinSell(changeDefaults.minSell);
            setMaxSell(changeDefaults.maxSell);
            setSelectedStrategyType(tab.value);
          }}
          btnClasses="text-14 !w-1/2"
          containerMargin={1}
        />
        <span className="text-14 text-white">{STRATEGY_DESCRIPTION[selectedStrategyType]}</span>
        <LpFundAccountCallout />
        <div className="flex w-full flex-col gap-y-4 pb-2">
          <TokenInputField
            value={baseAssetAmount}
            onChange={setBaseAssetAmount}
            token={baseAsset && chainflipAssetMap[baseAsset]}
            balance={baseAsset && freeBalances?.[baseAsset]}
            onTokenClick={() => setDisplayTokenModal(!displayTokenModal)}
            error={baseInputError}
            tokenWidth={180}
            disabled={isPending}
          />
          <TokenInputField
            value={quoteAssetAmount}
            onChange={setQuoteAssetAmount}
            token={chainflipAssetMap.Usdc}
            balance={freeBalances?.Usdc}
            error={quoteInputError}
            tokenWidth={180}
            disabled={isPending}
          />
        </div>
        <div className="flex w-full flex-col gap-y-3">
          <div className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-x-1 font-aeonikMedium text-14 text-cf-white">
              <span>Set Spread</span>
              <QuestionMarkTooltip content={TOOLTIP_COPY[selectedStrategyType].general} />
            </div>
          </div>
          <SpreadInputs
            strategyType={selectedStrategyType}
            minBuy={minBuy}
            maxBuy={maxBuy}
            minSell={minSell}
            maxSell={maxSell}
            setMinBuy={setMinBuy}
            setMaxBuy={setMaxBuy}
            setMinSell={setMinSell}
            setMaxSell={setMaxSell}
            errors={validation.fieldErrors}
            disabled={isNullish(baseAsset) || isPending}
          />
          {validation.message && (
            <span className="flex gap-x-1 text-12 text-cf-red-1">
              <WarningTriangleIcon />
              {validation.message}
            </span>
          )}
          {validation.warning && (
            <Callout type="warning" fullWidth>
              {validation.warning}
            </Callout>
          )}
          {existingInstance ? (
            <>
              <div className="text-12 text-cf-orange-1">
                This strategy already exists. You are adding liquidity now.
              </div>
              <StrategyOverview
                strategy={existingInstance}
                apyPercent={existingInstance.reportingData?.apyPercent}
                newBaseAssetAmount={
                  baseAssetAmount && existingInstance.baseLiquidity.add(baseAssetAmount)
                }
                newQuoteAssetAmount={
                  quoteAssetAmount && existingInstance.quoteLiquidity.add(quoteAssetAmount)
                }
              />
            </>
          ) : (
            <NewStrategyOverview
              type={selectedStrategyType}
              baseAsset={baseAsset}
              quoteAsset={quoteAsset}
              baseAssetAmount={baseAssetAmount}
              quoteAssetAmount={quoteAssetAmount}
              minBuy={minBuy}
              maxBuy={maxBuy}
              minSell={minSell}
              maxSell={maxSell}
              apyPercent={matchingTemplate?.reportingData?.apyPercent}
            />
          )}
        </div>
        <div className="h-px w-full bg-cf-gray-4" />
        <div className="flex flex-row justify-end gap-x-4">
          <Button
            onClick={mutate}
            loading={isPending && 'iconOnly'}
            disabled={isPending}
            className="order-2"
          >
            {existingInstance ? 'Add Liquidity' : 'Create'}
          </Button>
          <Button
            type="secondary-standard"
            onClick={onClose}
            disabled={isPending}
            className="order-1"
          >
            Cancel
          </Button>
        </div>
      </div>
    </DashboardModal>
  );
}
